import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth, useSignUp } from "@clerk/react";
import { codigoDoErro, mensagemDoErro } from "@/lib/painel/clerk-erros";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { CartaoEntrada } from "@/components/entrada/CartaoEntrada";
import { BotaoGoogle } from "@/components/entrada/BotaoGoogle";
import { CampoSenha } from "@/components/entrada/CampoSenha";
import { SeparadorOu } from "@/components/entrada/SeparadorOu";
import { SessaoAtiva } from "@/components/entrada/SessaoAtiva";

type Estagio = "formulario" | "codigo";

/** Criar conta — tela nossa, hooks do Clerk por baixo. Ver SignInForm. */
export function FormCadastro() {
  const { signUp, fetchStatus } = useSignUp();
  const signUpPronto = fetchStatus !== "fetching" && Boolean(signUp);
  const { isSignedIn } = useAuth();

  const [estagio, setEstagio] = useState<Estagio>("formulario");
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [codigoVerificacao, setCodigoVerificacao] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [googleCarregando, setGoogleCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ticket, setTicket] = useState<string | null>(null);

  // Convite: quem já tem conta vai para o login com o mesmo ticket.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get("__clerk_ticket");
    if (!t) return;
    const status = p.get("__clerk_status");
    if (status === "sign_in" || status === "complete") {
      window.location.replace(`/sign-in?__clerk_ticket=${encodeURIComponent(t)}`);
      return;
    }
    setTicket(t);
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const cod = p.get("erro") ?? p.get("err_code") ?? p.get("error");
    if (!cod) return;
    setErro(mensagemDoErro({ errors: [{ code: cod }] }));
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  async function cadastrar(e: React.FormEvent) {
    e.preventDefault();
    if (!signUpPronto || !signUp || ocupado) return;
    setOcupado(true);
    setErro(null);

    const partes = nome.trim().split(/\s+/);
    const firstName = partes[0] || "";
    const lastName = partes.slice(1).join(" ") || undefined;

    try {
      const created = await signUp.create(
        ticket
          ? { strategy: "ticket", ticket, password: senha, firstName, lastName }
          : { emailAddress: email.trim().toLowerCase(), password: senha, firstName, lastName },
      );
      if (created.error) {
        setErro(mensagemDoErro(created.error));
        setOcupado(false);
        return;
      }

      // Com ticket o e-mail já está provado: não há código de 6 dígitos.
      if (ticket) {
        const fin = await signUp.finalize({
          navigate: async ({ decorateUrl }) => {
            window.location.href = decorateUrl("/conversas");
          },
        });
        if (fin.error) {
          setErro(mensagemDoErro(fin.error));
          setOcupado(false);
        }
        return;
      }

      const sent = await signUp.verifications.sendEmailCode();
      if (sent.error) {
        setErro(mensagemDoErro(sent.error));
        setOcupado(false);
        return;
      }
      setEstagio("codigo");
      setOcupado(false);
    } catch (err) {
      setErro(mensagemDoErro(err));
      setOcupado(false);
    }
  }

  async function verificarCodigo(e: React.FormEvent) {
    e.preventDefault();
    if (!signUpPronto || !signUp || ocupado || !codigoVerificacao.trim()) return;
    setOcupado(true);
    setErro(null);
    try {
      const res = await signUp.verifications.verifyEmailCode({ code: codigoVerificacao.trim() });
      if (res.error) {
        setErro(mensagemDoErro(res.error));
        setOcupado(false);
        return;
      }
      const fin = await signUp.finalize({
        navigate: async ({ decorateUrl }) => {
          window.location.href = decorateUrl("/conversas");
        },
      });
      if (fin.error) {
        setErro(mensagemDoErro(fin.error));
        setOcupado(false);
      }
    } catch (err) {
      setErro(mensagemDoErro(err));
      setOcupado(false);
    }
  }

  async function cadastrarComGoogle() {
    if (!signUpPronto || !signUp || googleCarregando) return;
    setGoogleCarregando(true);
    setErro(null);
    try {
      if (ticket) {
        const comConvite = await signUp.create({ strategy: "ticket", ticket });
        if (comConvite.error) {
          setErro(mensagemDoErro(comConvite.error));
          setGoogleCarregando(false);
          return;
        }
      }
      const res = await signUp.sso({
        strategy: "oauth_google",
        redirectCallbackUrl: `${window.location.origin}/sign-up/sso-callback`,
        redirectUrl: `${window.location.origin}/conversas`,
      });
      if (res.error) {
        const cod = codigoDoErro(res.error);
        if (cod === "session_exists" || cod === "identifier_already_signed_in") {
          window.location.href = "/conversas";
          return;
        }
        setErro(mensagemDoErro(res.error));
        setGoogleCarregando(false);
      }
    } catch (err) {
      const cod = codigoDoErro(err);
      if (cod === "session_exists" || cod === "identifier_already_signed_in") {
        window.location.href = "/conversas";
        return;
      }
      setErro(mensagemDoErro(err));
      setGoogleCarregando(false);
    }
  }

  if (isSignedIn) return <SessaoAtiva modo="criar" />;

  if (estagio === "codigo") {
    return (
      <CartaoEntrada
        rotulo="Confirmação"
        titulo="Código enviado"
        descricao={
          <>
            Enviamos um código de 6 dígitos para <strong className="text-foreground">{email}</strong>.
          </>
        }
      >
        <form onSubmit={verificarCodigo}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="codigo">Código de 6 dígitos</FieldLabel>
              <Input
                id="codigo"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                autoFocus
                placeholder="123456"
                className="h-10 text-center font-mono text-lg tracking-[0.3em]"
                value={codigoVerificacao}
                onChange={(e) => setCodigoVerificacao(e.target.value)}
                disabled={ocupado}
              />
            </Field>

            {erro ? (
              <Alert variant="destructive">
                <AlertDescription>{erro}</AlertDescription>
              </Alert>
            ) : null}

            <Button type="submit" size="lg" className="w-full" disabled={ocupado || codigoVerificacao.length < 6}>
              {ocupado ? <Spinner /> : null}
              {ocupado ? "Verificando…" : "Confirmar e criar conta"}
            </Button>
            <Button type="button" variant="ghost" size="sm" className="w-full" onClick={() => setEstagio("formulario")}>
              Voltar e alterar dados
            </Button>
          </FieldGroup>
        </form>
      </CartaoEntrada>
    );
  }

  return (
    <CartaoEntrada
      rotulo="Cadastro"
      titulo="Criar conta"
      descricao={ticket ? "Complete seu cadastro para entrar na clínica." : "Crie o acesso da sua equipe no LummiChat."}
    >
      {!ticket ? (
        <>
          <BotaoGoogle rotulo="Cadastrar com o Google" carregando={googleCarregando} disabled={ocupado} onClick={() => void cadastrarComGoogle()} />
          <SeparadorOu />
        </>
      ) : null}

      <form onSubmit={cadastrar}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="nome">Seu nome completo</FieldLabel>
            <Input
              id="nome"
              required
              autoComplete="name"
              placeholder="Ex.: Dra. Camila Silva"
              className="h-11"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              disabled={ocupado}
            />
          </Field>

          {!ticket ? (
            <Field>
              <FieldLabel htmlFor="email">E-mail profissional</FieldLabel>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="voce@clinica.com"
                className="h-11"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={ocupado}
              />
            </Field>
          ) : null}

          <Field>
            <FieldLabel htmlFor="senha">Senha (mínimo 8 caracteres)</FieldLabel>
            <CampoSenha
              id="senha"
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="••••••••"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              disabled={ocupado}
            />
          </Field>

          {erro ? (
            <Alert variant="destructive">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          ) : null}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={ocupado || !nome.trim() || (!ticket && !email.trim()) || senha.length < 8}
          >
            {ocupado ? <Spinner /> : null}
            {ocupado ? "Criando conta…" : "Criar conta"}
          </Button>
        </FieldGroup>
      </form>

      <p className="mt-5 text-center text-xs text-muted-foreground">
        Já tem uma conta?{" "}
        <Link to="/sign-in" className="font-semibold text-primary hover:underline">
          Fazer login
        </Link>
      </p>

      <div id="clerk-captcha" />
    </CartaoEntrada>
  );
}
